import {
  DbRecentResult,
  type DbSnapshot,
  type ServiceSummary,
  type TxSummary,
} from '@apm/contracts';
import { useEffect, useMemo, useState } from 'react';
import { useAccess } from '../auth/access';
import { useCoalesced } from '../hooks/useCoalesced';
import { usePolling } from '../hooks/usePolling';
import { useQuery } from '../hooks/useQuery';
import { routeHref } from '../router';
import { useServices } from '../services/ServiceProvider';
import { useServiceFilter } from '../services/service-filter';
import { statusText } from '../status';
import { streamStore } from '../store/stream';
import { useLiveState } from '../store/useStream';
import { TIP } from '../terms';
import {
  formatCount,
  formatDuration,
  formatFull,
  formatPercent,
  formatRelative,
  formatStamp,
} from '../time';
import {
  Cell,
  Chip,
  type Column,
  EmptyState,
  Loading,
  Page,
  Row,
  Section,
  Stat,
  Table,
  Toggle,
  Tooltip,
} from '../ui';
import { ActiveTable } from './ActiveTable';
import { flattenActive, mergeSse } from './active';
import { SLOW_MS } from './activity';
import { ConnectionStatus } from './ConnectionStatus';
import { DbPanel } from './DbPanel';
import { countErrors } from './errors';
import { TX_TYPE_TONE } from './format';
import { Gauges } from './Gauges';
import { RateTrendSection } from './RateTrendSection';
import { lastRowsOf } from './recent';
import { TrafficSection } from './TrafficSection';

/**
 * 화면 1 — 지금 도는 요청.
 *
 * 앱 상태 · 진행 중 목록 · DB · 실시간 연결 네 구획으로 나눈다.
 * 웹 요청·MCP·PDF·정기 작업은 한 표로, 실시간 연결은 건수만 따로 낸다.
 *
 * DB 구획(9회차)은 같은 순간의 대상 DB 세션이다. 앱 쪽에서 "DB를 기다리는 중"으로만 보이던
 * 요청이 DB 안에서 무엇을 기다리는지 바로 아래에서 보인다.
 *
 * 19회차에 서비스 고르기(후보 #2)와 오류만 보기(C-3)가 붙었다. 둘 다 화면에서 거른다 —
 * 서버에 `?service=`를 붙이면 실시간 연결을 끊고 다시 붙여야 하는데, 이미 받아 둔 것을
 * 거르는 편이 즉시 반응하고 연결도 흔들지 않는다.
 * 22-B회차에 최근 1시간 초당 요청·오류 추이가 인스턴스 상태 아래 붙었다(빠진 정보 5).
 * 23-A회차에 진행 중 요청 구획이 **최근 1분 최대 동시 처리 수·3초 넘긴 요청 수**를 함께 낸다
 * (UI 피드백 23). 이 목록은 평소에 늘 0건이라 그것만 보면 화면이 죽은 것처럼 보인다.
 * 그 요약은 스냅샷이 아니라 **시간**으로도 만료돼야 한다(23-A회차 리뷰 7) — 보고가 끊기면
 * 새 스냅샷이 없어 다시 셀 일이 없고 1분 전 값이 화면에 그대로 남는다. 1초 tick으로 다시 센다.
 * 고른 서비스도 함께 넘긴다. 같은 구획의 표가 그 서비스만 보기 때문이다.
 *
 * 34회차에 **트레이스만 서비스 화면**이 안내 한 줄에서 벗어났다. 프로세스 값(메모리·처리
 * 지연·진행 중 요청)은 여전히 없지만, 초당 요청·오류·응답시간은 끝난 요청으로 만들 수 있어
 * 서버가 접어 준 것(`GET /api/rt/traffic`)을 그린다. 실시간 서비스 화면은 그대로다.
 */
export function LivePage() {
  const state = useLiveState();
  const service = useServiceFilter();
  const services = useServices();
  const [errorsOnly, setErrorsOnly] = useState(false);

  const instances = useMemo(
    () => state.instances.filter((item) => service === '' || item.service === service),
    [state.instances, service],
  );
  const silent = useMemo(
    () => state.silent.filter((item) => service === '' || item.service === service),
    [state.silent, service],
  );

  // 스냅샷이 바뀐 프레임에만 다시 편다
  const allRows = useMemo(() => flattenActive(instances), [instances]);
  const rows = useMemo(
    () => (errorsOnly ? allRows.filter((row) => row.elapsedMs >= 3_000) : allRows),
    [allRows, errorsOnly],
  );
  const sse = useMemo(() => mergeSse(instances), [instances]);
  // 목록 자체는 상태에 없다(5분에 수만 건). 판 번호가 바뀐 프레임에만 다시 센다.
  // 판은 거의 프레임마다 오르므로 1초에 한 번으로 묶는다 (D-C5)
  const txVersion = useCoalesced(state.txVersion, 1_000);
  const errors = useMemo(
    () => countErrors(streamStore.getRows(), streamStore.windowMs, Date.now()),
    [txVersion],
  );
  // 최근 1분 요약. 스냅샷이 올 때와 1초마다 다시 센다 (23-A회차 리뷰 7)
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setTick((value) => value + 1), 1_000);
    return () => clearInterval(timer);
  }, []);
  const activity = useMemo(
    () => streamStore.getActivity().summary(Date.now(), service),
    [state.activityVersion, tick, service],
  );
  // DB 세션이 어느 요청 것인지 보여 주려면 지금 도는 요청의 요청 번호 → 경로가 필요하다
  const entries = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of allRows) map.set(row.traceId, row.entry);
    return map;
  }, [allRows]);

  const selectedSummary = services.data?.rows.find((item) => item.service === service);
  const selectedPresence = state.presence.filter((item) => item.service === service);
  const traceOnly =
    service !== '' &&
    (selectedSummary?.realtime === false ||
      (selectedPresence.length > 0 && selectedPresence.every((item) => !item.realtime)));
  // 뒤에서부터 10건만 찾는다. 5만 행 전체를 거르지 않는다 (D-C5)
  const recentRows = useMemo(
    () => lastRowsOf(streamStore.getRows(), service, 10),
    [state.txVersion, service],
  );

  // 첫 서비스 목록을 받기 전에는 빈 인스턴스 화면을 잠깐 그리지 않는다.
  if (service !== '' && services.data === undefined && services.loading) {
    return (
      <Page>
        <ConnectionStatus stream={state.status} />
        <Loading />
      </Page>
    );
  }

  if (traceOnly) {
    return (
      <TraceOnlyPage
        status={state.status}
        service={service}
        summary={selectedSummary}
        rows={recentRows}
        db={state.db}
        entries={entries}
      />
    );
  }

  return (
    <Page>
      <ConnectionStatus stream={state.status} />

      <Section
        title="인스턴스 상태"
        note="에이전트가 1초마다 보내는 값. 프로세스(인스턴스) 하나가 카드 하나다"
        badge={`보고 중 ${instances.length}개${silent.length > 0 ? ` · 끊김 ${silent.length}개` : ''}`}
      >
        <Gauges
          instances={instances}
          silent={silent}
          presence={state.presence.filter((item) => service === '' || item.service === service)}
          errors={errors}
          grouped={service === ''}
        />
      </Section>

      <RateTrendSection
        trend={streamStore.getTrend()}
        version={state.trendVersion}
        service={service}
      />

      <Section
        title="진행 중 요청"
        note={[
          '경과시간 내림차순. 실시간 연결은 아래 따로 센다.',
          activity.seconds === 0
            ? '최근 1분 요약은 보고를 받는 대로 채워진다'
            : `최근 1분 최대 동시 처리 ${formatCount(activity.maxConcurrent)}건 · ` +
              `${SLOW_MS / 1_000}초 넘긴 요청 ${formatCount(activity.slowCount)}건`,
        ]}
        badge={`${formatCount(rows.length)}건`}
        actions={
          <Toggle
            id="live-slow-only"
            checked={errorsOnly}
            onChange={setErrorsOnly}
            label="3초 이상만"
            hint="진행 중인 요청에는 아직 상태 코드가 없다. 대신 경과시간으로 거른다"
          />
        }
      >
        <ActiveTable
          rows={rows}
          filtered={errorsOnly && allRows.length !== rows.length}
          showService={service === ''}
        />
      </Section>

      <DbSection db={state.db} entries={entries} />

      <Section title="실시간 연결" note="연결이 수십 분 유지되므로 위 목록에 섞지 않는다">
        <div className="flex flex-wrap items-center gap-x-8 gap-y-3 rounded-xl border border-hair bg-panel px-5 py-4">
          <div>
            <p className="text-xs text-muted">열린 연결</p>
            <p className="tabular mt-1 text-2xl font-semibold text-sse">
              {formatCount(sse.count)}건
            </p>
          </div>
          <div>
            <p className="text-xs text-muted">최장 연결</p>
            <p className="tabular mt-1 text-sm text-ink">{formatDuration(sse.maxElapsedMs)}</p>
          </div>
        </div>
      </Section>
    </Page>
  );
}

const RECENT_COLUMNS: readonly Column[] = [
  { label: '시각', width: 'w-40' },
  { label: '유형', width: 'w-32' },
  { label: '요청 경로' },
  { label: '응답시간', width: 'w-28' },
  { label: '상태', width: 'w-28' },
];

/**
 * 트레이스만 서비스의 실시간 화면 (27회차, 34회차에 값이 붙었다).
 *
 * 프로세스 값(메모리·처리 지연·진행 중 요청)은 이 연결 방식으로 만들 수 없다 — 트레이스는
 * 요청이 **끝난 뒤에** 배치로 오기 때문이다. 반면 초당 완료·오류·응답시간은 끝난 요청만으로
 * 계산할 수 있어 서버가 접어 준 것을 그린다.
 *
 * 요약 카드는 `GET /api/services`가 이미 주는 5분 창을 쓴다. 같은 값을 두 곳에서 따로 세면
 * 서비스 목록의 카드와 이 화면의 숫자가 어긋난다.
 */
function TraceOnlyPage({
  status,
  service,
  summary,
  rows,
  db,
  entries,
}: {
  status: Parameters<typeof ConnectionStatus>[0]['stream'];
  service: string;
  /** 서비스 목록의 그 서비스 줄. 아직 못 읽었으면 없다 */
  summary?: ServiceSummary;
  rows: readonly TxSummary[];
  db?: DbSnapshot;
  entries: ReadonlyMap<string, string>;
}) {
  const quiet = summary !== undefined && summary.last5m.requests === 0;
  const lastRequest = lastRequestTile(summary);
  return (
    <Page>
      <ConnectionStatus stream={status} />
      <Section
        boxed
        title="앱 상태"
        note={[
          `${service}은 표준 OTel로 붙어 1초 보고를 보내지 않는다.`,
          '끝난 요청으로 만들 수 있는 값만 낸다. 메모리와 처리 지연 같은 프로세스 값은 이 연결 방식에서 보내지 않는다',
        ]}
        badge={quiet ? '조용함' : undefined}
      >
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Stat
            label="마지막 요청"
            value={lastRequest.value}
            note={
              lastRequest.note ?? (quiet ? '요청이 없는 것이지 연결이 끊긴 것이 아니다' : undefined)
            }
          />
          <Stat
            label="최근 5분 요청"
            value={`${formatCount(summary?.last5m.requests ?? 0)}건`}
            note="실시간 연결은 뺀 수"
          />
          {/* 요청이 0건이면 오류율·응답시간은 0이 아니라 잴 것이 없는 것이다 */}
          <Stat
            label="최근 5분 오류율"
            value={quiet ? '-' : formatPercent(summary?.last5m.errorRate ?? 0)}
            level={!quiet && (summary?.last5m.errorRate ?? 0) > 0 ? 'warn' : 'normal'}
            warnLabel="오류 있음"
          />
          <Stat
            label="최근 5분 응답시간(느린 5%)"
            value={quiet ? '-' : formatDuration(summary?.last5m.p95Ms ?? 0)}
          />
        </div>
      </Section>
      <TrafficSection service={service} />
      <Section boxed title="최근 요청" note="실시간으로 받은 요청 중 마지막 10건">
        {rows.length === 0 ? (
          <EmptyState title="최근 요청이 없다" hint="요청을 보낸 뒤 잠시 기다려 볼 것" />
        ) : (
          <Table columns={RECENT_COLUMNS} caption={`${service} 최근 요청`} minWidth="min-w-[760px]">
            {rows.map((row) => (
              <Row key={row.txId}>
                <Cell nowrap tone="muted" className="tabular">
                  <a
                    href={routeHref({
                      name: 'detail',
                      traceId: row.traceId,
                      from: 'requests',
                      service: row.service,
                    })}
                    title={formatFull(row.startedAt)}
                    className="text-link no-underline hover:underline"
                  >
                    {formatStamp(row.startedAt)}
                  </a>
                </Cell>
                <Cell>
                  <Chip tone={TX_TYPE_TONE[row.txType]}>{row.txType}</Chip>
                </Cell>
                <Cell mono title={row.entry} className="max-w-[420px] truncate">
                  {row.entry}
                </Cell>
                <Cell mono nowrap className="tabular">
                  {formatDuration(row.durationMs)}
                </Cell>
                <Cell>{statusText(row)}</Cell>
              </Row>
            ))}
          </Table>
        )}
      </Section>
      <DbSection db={db} entries={entries} />
    </Page>
  );
}

/**
 * "마지막 요청" 타일 (34회차 리뷰 반영).
 *
 * 관제 서버가 다시 뜨면 마지막 요청 시각의 기억이 비어 "받은 적 없음"으로 보인다. 어제까지
 * 잘 돌던 앱이 처음 보는 앱처럼 보이는 자리라, 저장에 남은 마지막 기록으로 답할 때는
 * 그렇다고 적고, 그것마저 없으면 "알 수 없음"과 "받은 적 없음"을 가른다.
 */
function lastRequestTile(summary?: ServiceSummary): { value: string; note?: string } {
  if (summary === undefined) return { value: '읽는 중' };
  if (summary.lastSeenAt === undefined) {
    const stored = summary.storage.hotBytes + summary.storage.coldBytes > 0;
    return stored
      ? { value: '알 수 없음', note: '관제 서버가 다시 뜬 뒤로 받은 요청이 없다' }
      : { value: '받은 적 없음' };
  }
  if (summary.lastSeenFromStorage === true) {
    return {
      value: formatRelative(summary.lastSeenAt),
      note: '관제 서버가 다시 뜬 뒤로 받은 요청이 없다. 저장에 남은 마지막 기록이다',
    };
  }
  return { value: formatRelative(summary.lastSeenAt) };
}

export function DbSection({
  db,
  entries,
}: {
  db?: DbSnapshot;
  entries: ReadonlyMap<string, string>;
}) {
  // DB 감시는 관리자만 본다 (52회차). 서버도 일반 계정에는 DB 스냅샷을 보내지 않는다
  const { admin } = useAccess();
  const version = usePolling(admin ? 30_000 : 0);
  const watcher = useQuery(admin ? '/api/db/recent?minutes=1' : undefined, DbRecentResult, version);
  if (!admin) return null;
  // 처음 화면을 열었거나 SSE가 재연결 중이면 조회 응답의 마지막 스냅샷도 쓸 수 있다.
  // 두 통로 중 더 최근 값을 고르되 stream.ts와 같은 10초 상한을 적용한다.
  // LivePage의 1초 tick으로 다시 그리므로 API를 다시 읽기 전에도 오래된 값은 사라진다.
  // 연결이 끊긴 경우에는 DbPanel이 옛 값을 정상으로 내지 않는다.
  const recent = watcher.data?.snapshots.reduce<DbSnapshot | undefined>(
    (latest, item) => (latest === undefined || item.ts > latest.ts ? item : latest),
    undefined,
  );
  const latest = recent !== undefined && (db === undefined || recent.ts > db.ts) ? recent : db;
  const snapshot = latest !== undefined && Date.now() - latest.ts <= 10_000 ? latest : undefined;
  const ready =
    watcher.error === undefined &&
    watcher.data?.stats.connected === true &&
    watcher.data.stats.enabled;
  return (
    <Section
      title="DB 감시"
      note={[
        '대상 DB의 세션을 1초마다 조회한다. 고른 앱과 상관없이 늘 같은 대상 DB 하나를 본다.',
        <>
          <Tooltip text={TIP.dbTraceComment}>쿼리를 보낸 요청</Tooltip>과 이어서 보인다
        </>,
      ]}
      badge={ready && snapshot ? `${snapshot.sessions.length}건` : undefined}
    >
      <DbPanel
        db={snapshot}
        entries={entries}
        watcher={watcher.data?.stats}
        error={watcher.error}
      />
    </Section>
  );
}
